import React, {useCallback, useEffect, useState} from "react";
import {GoogleMap, Marker, OverlayView, Polygon, Polyline} from "@react-google-maps/api";
import {insertNotification} from "Utils/NotificationsUtils";
import PropTypes from "prop-types";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faLocationCrosshairs, faUnlock} from "@fortawesome/free-solid-svg-icons";
import {Button} from "react-bootstrap";
import {
    AnnotationShapeIDMap,
    AnnotationType,
    AuditItemTypeInitialFilters,
    AuditStatusID,
} from "Models/Audit/AuditConstants";
import {
    AreaDrawingOptions,
    AreaIcon,
    baseInputStyle,
    cancelButtonStyle,
    containerStyle,
    crosshairContainerStyle,
    crosshairStyle,
    currentAreaAndDistanceStyle,
    currentLocationButtonStyle,
    dragPinButtonStyle,
    getAreaAnnotationStyle,
    getItemStyle,
    getLineAnnotationStyle,
    getPinAnnotationStyle,
    hideAllButtonStyle,
    hoverItemStyle,
    LineCrosshairInnerIcon,
    LineDrawingOptions,
    locationButtonStyle,
    locationInputStyle,
    locationLabelStyle,
    saveButtonStyle,
    undoButtonStyle,
} from "./GSPSGoogleMap.styles";
import "./GSPSGoogleMap.css";
import {FaSave, FaUndo} from "react-icons/fa";
import {MdAutoDelete} from "react-icons/md";
import {
    getAreaLabelPosition,
    getLineLabelPosition,
    getPathAreaInFt,
    getPathLengthInFt,
    getPointLatLngFromEvent,
    useAppleDevice,
} from "Components/Map/SVPGoogleMapUtils";
import {isFeatureSwitchActive} from "Utils/WaffleJSHelper";
import {setNearMap} from "Components/Map/SVPNearMap";
import mapCrosshairIconMobile from "./map-crosshair-mobile.png";
import {
    getLatLngFromScreen,
    getMobileCrosshairPosition,
    getMobileDragOffset,
} from "Components/Map/SVPGoogleMapMobileUtils";

const DrawingMode = {
    POLYLINE: "polyline",
    POLYGON: "polygon",
};


export const GSPSGoogleMap = ({
    audit,
    currentLocation,
    annotationAreas,
    annotationLines,
    annotationMarkers,
    onLineClick,
    onIssueClick,
    onAreaClick,
    saveIssue = null,
    saveLine = null,
    saveArea = null,
    setMapClicked = null,
    currentAuditType = null,
    currentAnnotationShape = null,
    currentAuditTypeItem = null,
    handleDropDownResetDone = null,
    issuesSeveritiesWithColor = null,
    updateIssue,
    isViewOnly = false,
    auditsFilter = AuditItemTypeInitialFilters,
}) => {
    const [currentDrawingMode, setCurrentDrawingMode] = useState(null);
    const [map, setMap] = useState(null);
    const [hoveredItem, setHoveredItem] = useState(null);
    const [linePoints, setLinePoints] = useState([]);
    const [currentLineLength, setCurrentLineLength] = useState(null);
    const [areaPoints, setAreaPoints] = useState([]);
    const [calculatedAreaInFt, setCalculatedAreaInFt] = useState(null);
    const [currentClickedLocation, setCurrentClickedLocation] = useState(currentLocation);
    const [isShowLocationMarker, setIsShowLocationMarker] = useState(false);
    const [isHideAllAnnotations, setIsHideAllAnnotations] = useState(false);
    const [isItemDraggable, setIsItemDraggable] = useState(false);
    const [drawingMousePosition, setDrawingMousePosition] = useState(null);
    const isMobileDrawing = useAppleDevice();
    const [isSavingLineOrArea, setIsSavingLineOrArea] = useState(false);
    const [isDisableDragButton, setIsDisableDragButton] = useState(false);

    const [mobileCrosshairPosition, setMobileCrosshairPosition] = useState(null);
    const [isMobileDraggingCrosshair, setIsMobileDraggingCrosshair] = useState(false);
    const [mobileDragOffset, setMobileDragOffset] = useState({x: 0, y: 0});
    const [hasMobileDraggedCrosshair, setHasMobileDraggedCrosshair] = useState(false);

    const isDisableMobileCrosshair = useCallback(() => {
        return !isMobileDrawing || !currentDrawingMode;
    }, [currentDrawingMode, isMobileDrawing]);

    useEffect(() => {
        if (isDisableMobileCrosshair()) {
            setMobileCrosshairPosition(null);
            setIsMobileDraggingCrosshair(false);
            setHasMobileDraggedCrosshair(false);
            setMobileDragOffset({x: 0, y: 0});
            return;
        }
        if (!map || mobileCrosshairPosition) return;

        const mapCenter = map.getCenter();
        setMobileCrosshairPosition({
            lat: mapCenter.lat(),
            lng: mapCenter.lng(),
        });
    }, [map, mobileCrosshairPosition, isDisableMobileCrosshair]);

    const mobileScreenToLatLng = useCallback((x, y) => {
        if (isDisableMobileCrosshair()) return;
        if (!map) return;

        return getLatLngFromScreen(map, x, y);
    }, [isDisableMobileCrosshair, map]);

    const handleMobileCrosshairMouseDown = useCallback((event) => {
        if (isDisableMobileCrosshair()) return;

        event.preventDefault();
        event.stopPropagation();
        setIsMobileDraggingCrosshair(true);
        setHasMobileDraggedCrosshair(false);

        const offset = getMobileDragOffset(event);

        setMobileDragOffset(offset);
    }, [isDisableMobileCrosshair]);

    const handleMobileCrosshairMouseMove = useCallback((event) => {
        if (isDisableMobileCrosshair()) return;
        if (!isMobileDraggingCrosshair) return;

        event.preventDefault();
        event.stopPropagation();

        setHasMobileDraggedCrosshair(true);

        const {x, y} = getMobileCrosshairPosition(event, mobileDragOffset);
        const newLatLng = mobileScreenToLatLng(x, y);

        if (newLatLng) {
            const newPosition = {
                lat: newLatLng.lat(),
                lng: newLatLng.lng(),
            };
            setMobileCrosshairPosition(newPosition);
        }
    }, [isDisableMobileCrosshair, isMobileDraggingCrosshair, mobileDragOffset, mobileScreenToLatLng]);


    const handleMobileCrosshairMouseUp = useCallback(async (event) => {
        if (isDisableMobileCrosshair()) return;
        if (!isMobileDraggingCrosshair) return;

        event.preventDefault();
        event.stopPropagation();
        setIsMobileDraggingCrosshair(false);
        setMobileDragOffset({x: 0, y: 0});

        if (!mobileCrosshairPosition) return;

        const drawingPointLatLng = {lat: mobileCrosshairPosition.lat, lng: mobileCrosshairPosition.lng};
        if ((linePoints.length === 0 && currentDrawingMode === DrawingMode.POLYLINE) ||
            (areaPoints.length === 0 && currentDrawingMode === DrawingMode.POLYGON)) {
            setDrawingMousePosition(drawingPointLatLng);
        }

        if (hasMobileDraggedCrosshair) return;

        if (currentDrawingMode === DrawingMode.POLYLINE) {
            const path = [...linePoints, drawingPointLatLng];

            if (path.length >= 2) {
                setCurrentLineLength(getPathLengthInFt(path).toFixed(2));
            } else {
                setCurrentLineLength(null);
            }
            setLinePoints(path);
        } else if (currentDrawingMode === DrawingMode.POLYGON) {
            const newArea = [...areaPoints, drawingPointLatLng];

            if (newArea.length >= 3) {
                setCalculatedAreaInFt(getPathAreaInFt(newArea).toFixed(2));
            } else {
                setCalculatedAreaInFt(null);
            }
            setAreaPoints(newArea);
        }
    }, [isDisableMobileCrosshair, isMobileDraggingCrosshair, mobileCrosshairPosition, linePoints,
        currentDrawingMode, hasMobileDraggedCrosshair, areaPoints]);

    useEffect(() => {
        if (isDisableMobileCrosshair()) return;
        if (!isMobileDraggingCrosshair) return;

        document.addEventListener("mousemove", handleMobileCrosshairMouseMove, {passive: false});
        document.addEventListener("mouseup", handleMobileCrosshairMouseUp);
        document.addEventListener("touchmove", handleMobileCrosshairMouseMove, {passive: false});
        document.addEventListener("touchend", handleMobileCrosshairMouseUp);

        return () => {
            document.removeEventListener("mousemove", handleMobileCrosshairMouseMove);
            document.removeEventListener("mouseup", handleMobileCrosshairMouseUp);
            document.removeEventListener("touchmove", handleMobileCrosshairMouseMove);
            document.removeEventListener("touchend", handleMobileCrosshairMouseUp);
        };
    }, [isMobileDraggingCrosshair, handleMobileCrosshairMouseMove, handleMobileCrosshairMouseUp,
        isDisableMobileCrosshair]);

    useEffect(() => {
        if (isDisableMobileCrosshair()) return;

        const mapContainer = document.getElementById("map-container");
        if (isMobileDraggingCrosshair && mapContainer) {
            mapContainer.style.touchAction = "none";
            document.body.style.touchAction = "none";
        } else if (mapContainer) {
            mapContainer.style.touchAction = "";
            document.body.style.touchAction = "";
        }

        return () => {
            if (mapContainer) {
                mapContainer.style.touchAction = "";
            }
            document.body.style.touchAction = "";
        };
    }, [isDisableMobileCrosshair, isMobileDraggingCrosshair]);

    const resetArea = () => {
        setAreaPoints([]);
        setCalculatedAreaInFt(null);
    };

    const resetLine = () => {
        setLinePoints([]);
        setCurrentLineLength(null);
    };

    const handleUndoDrawing = () => {
        if (currentDrawingMode === DrawingMode.POLYGON && areaPoints.length > 0) {
            const newPoints = areaPoints.slice(0, -1);
            if (newPoints.length < 3) {
                handleCancelDrawing();
            } else {
                setAreaPoints(newPoints);
            }
            if (newPoints.length >= 3) {
                setCalculatedAreaInFt(getPathAreaInFt(newPoints).toFixed(2));
            } else {
                setCalculatedAreaInFt(null);
            }
            setDrawingMousePosition(null);
        } else if (currentDrawingMode === DrawingMode.POLYLINE && linePoints.length > 0) {
            const newPoints = linePoints.slice(0, -1);
            setLinePoints(newPoints);
            if (newPoints.length > 1) {
                setCurrentLineLength(getPathLengthInFt(newPoints).toFixed(2));
                setDrawingMousePosition(null);
            } else {
                handleCancelDrawing();
            }
        }
    };

    const getLatLngFromEvent = useCallback((event) => {
        if (!map) return null;
        return getPointLatLngFromEvent(event, map);
    }, [map]);

    const handleDesktopAddLinePoint = useCallback((event) => {
        const latLng = getLatLngFromEvent(event);
        if (latLng) {
            const path = [...linePoints, {lat: latLng.lat(), lng: latLng.lng()}];
            setDrawingMousePosition({lat: latLng.lat(), lng: latLng.lng()});
            if (linePoints.length >= 1) {
                setCurrentLineLength(getPathLengthInFt(path).toFixed(2));
            } else {
                setCurrentLineLength(null);
            }
            setLinePoints(path);
        }
    }, [getLatLngFromEvent, linePoints]);

    const handleDesktopAddAreaPoint = useCallback((event) => {
        const latLng = getLatLngFromEvent(event);
        if (latLng) {
            const newPoint = {lat: latLng.lat(), lng: latLng.lng()};
            const newArea = [...areaPoints, newPoint];
            setDrawingMousePosition(newPoint);
            if (areaPoints.length >= 2) {
                setCalculatedAreaInFt(getPathAreaInFt(newArea).toFixed(2));
            } else {
                setCalculatedAreaInFt(null);
            }
            setAreaPoints(newArea);
        }
    }, [getLatLngFromEvent, areaPoints]);

    const handleDesktopAddPin = useCallback(async (event) => {
        const isAnnotationOptionSelected = currentAuditType && currentAuditTypeItem;
        if (!isAnnotationOptionSelected || currentAnnotationShape?.query !== AnnotationShapeIDMap.PIN.query) {
            return;
        }

        const latLng = getLatLngFromEvent(event);
        await saveIssue({lat: latLng.lat(), lng: latLng.lng()});
        handleDropDownResetDone();
        setMapClicked(true);
    }, [currentAnnotationShape?.query, currentAuditType, currentAuditTypeItem, getLatLngFromEvent,
        handleDropDownResetDone, saveIssue, setMapClicked]);

    useEffect(() => {
        if (!isDisableMobileCrosshair()) {
            return;
        }

        if (!map) {
            setDrawingMousePosition(null);
            return;
        }

        const drawingListener = google.maps.event.addDomListener(
            map.getDiv(),
            "mousedown",
            !isMobileDrawing && !currentDrawingMode ? handleDesktopAddPin :
                currentDrawingMode === DrawingMode.POLYGON ? handleDesktopAddAreaPoint : handleDesktopAddLinePoint,
        );

        return () => {
            google.maps.event.removeListener(drawingListener);
        };
    }, [map, currentDrawingMode, handleDesktopAddAreaPoint, handleDesktopAddLinePoint,
        isDisableMobileCrosshair, handleDesktopAddPin, isMobileDrawing]);

    useEffect(() => {
        if (map) {
            map.addListener("zoom_changed", handleZoomChange);
        }
        return () => {
            if (map) {
                google.maps.event.clearListeners(map, "zoom_changed");
            }
        };
    }, [map]);

    useEffect(() => {
        const isAnnotationOptionSelected = currentAuditType && currentAuditTypeItem;
        const isAnnotationTypeSelected = currentAnnotationShape?.query === AnnotationType.AREA ||
            currentAnnotationShape?.query === AnnotationType.LINE;

        const newDrawingMode = isAnnotationTypeSelected && isAnnotationOptionSelected ?
            currentAnnotationShape?.query === AnnotationType.AREA ?
                DrawingMode.POLYGON :
                DrawingMode.POLYLINE :
            null;

        setCurrentDrawingMode(newDrawingMode);
        resetLine();
        resetArea();
    }, [currentAnnotationShape, currentAuditType, currentAuditTypeItem]);

    const onMapClick = useCallback(async (event) => {
        if (!currentAuditType || !currentAnnotationShape || !currentAuditTypeItem) {
            insertNotification(
                "Error", "Please select Audit Type, Item Type and Item first", "error",
            );
            return;
        }

        if (isMobileDrawing && !currentDrawingMode &&
            currentAnnotationShape?.query === AnnotationShapeIDMap.PIN.query) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            await saveIssue({lat, lng});
            handleDropDownResetDone();
            setMapClicked(true);
        }
    }, [currentAnnotationShape, currentAuditType, currentAuditTypeItem, currentDrawingMode,
        handleDropDownResetDone, isMobileDrawing, saveIssue, setMapClicked]);

    const handleZoomChange = () => {
        setHoveredItem(null);
    };

    const handleCancelDrawing = () => {
        resetArea();
        resetLine();
        setDrawingMousePosition(null);
    };

    const onPinDragEnd = useCallback((event, pin) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        const newPin = {
            ...pin,
            address_lnglat: {...pin.address_lnglat, lat: lat, lng: lng},
            audit_type_item_id: pin.audit_type_item.id,
        };
        updateIssue(newPin);
    }, [updateIssue]);

    const createCustomIcon = (marker) => ({
        path: "M 0,0 m -10,0 a 10,10 0 1,0 20,0 a 10,10 0 1,0 -20,0",
        fillColor: marker.custom_color ?? marker.audit_type_item?.color,
        fillOpacity: 1,
        strokeColor: getBorderColor(marker),
        strokeWeight: 3,
        scale: 1.5,
    });

    const onLoad = (map) => {
        setMap(map);

        if (isFeatureSwitchActive("can_user_nearmap_tiles")) {
            setNearMap(map);
        }
    };

    const onSaveDrawing = async () => {
        setIsSavingLineOrArea(true);

        try {
            if (!currentAuditType || !currentAnnotationShape || !currentAuditTypeItem) {
                insertNotification(
                    "Error",
                    "Please select Audit Type, Item Type and Item first",
                    "error",
                );
                return;
            }

            if (currentDrawingMode === DrawingMode.POLYGON) {
                if (areaPoints.length < 3) {
                    insertNotification("Error", "An area must have at least three points.", "error");
                    return;
                }
                const areaInSquareFeet = getPathAreaInFt(areaPoints);
                await saveArea({path: areaPoints, areaInSquareFeet});

                setCurrentDrawingMode(null);
                handleDropDownResetDone();
                setMapClicked(true);
                resetArea();
                setDrawingMousePosition(null);
            } else if (currentDrawingMode === DrawingMode.POLYLINE) {
                if (linePoints.length < 2) {
                    insertNotification("Error", "A line must have at least two points.", "error");
                    return;
                }
                const lengthInFeet = getPathLengthInFt(linePoints);
                await saveLine(linePoints, lengthInFeet);
                setCurrentDrawingMode(null);
                handleDropDownResetDone();
                setMapClicked(true);
                resetLine();
                setDrawingMousePosition(null);
            }
        } catch (error) {
            insertNotification("Error", "Something went wrong while saving the shape", "error");
        } finally {
            setIsSavingLineOrArea(false);
        }
    };

    const goToCurrentLocation = useCallback(() => {
        if (map) {
            const successCallback = (position) => {
                const {latitude, longitude} = position.coords;
                const newCenter = {lat: latitude, lng: longitude};
                map.panTo(newCenter);
                setCurrentClickedLocation(newCenter);
                setIsShowLocationMarker(true);
            };

            const errorCallback = () => {
                insertNotification("Error",
                    "Unable to detect your location. Enable location permission for the browser.",
                    "error");
            };
            navigator.geolocation.getCurrentPosition(successCallback, errorCallback, {
                maximumAge: 0, timeout: 5000, enableHighAccuracy: true,
            });
        }
        // eslint-disable-next-line
    }, [map]);

    const getZoomLevel = () => {
        if (Number(audit.audit_status.id) !== AuditStatusID.IN_PROGRESS &&
            (annotationAreas.length > 0 || annotationLines.length > 0 || annotationMarkers.length > 0)
        ) {
            return 18;
        } else {
            return 20;
        }
    };

    useEffect(() => {
        if (
            currentDrawingMode === DrawingMode.POLYLINE ||
                currentDrawingMode === DrawingMode.POLYGON
        ) {
            setIsDisableDragButton(true);
        } else {
            setIsDisableDragButton(false);
        }

        setIsItemDraggable(false);
    }, [currentDrawingMode]);

    function getBorderColor(annotation) {
        return isViewOnly ?
            annotation.severity_object?.color_code :
            issuesSeveritiesWithColor?.find((issueSeverity) =>
                issueSeverity.id === Number(annotation.severity))?.color_code;
    }

    function getItemBackgroundColor() {
        return issuesSeveritiesWithColor?.find(
            (severity) => severity.id === Number(hoveredItem.severity),
        )?.color_code;
    }

    const onItemMouseOver = (item) => {
        setHoveredItem(item);
    };
    const onItemMouseOut = () => {
        setHoveredItem(null);
    };

    const renderPins = () => {
        return annotationMarkers.map((marker, index) => {
            const position = {lat: Number(marker.address_lnglat.lat), lng: Number(marker.address_lnglat.lng)};
            return (((auditsFilter.pins?.includes(marker.audit_type_item.name?.trim()) &&
                    auditsFilter.severity?.includes(marker.severity_object.name?.trim())) ||
                auditsFilter.default) && (
                <React.Fragment key={`${index}-${isItemDraggable}`}>
                    <Marker
                        position={position}
                        key={`marker-${index}-${isItemDraggable}`}
                        draggable={isItemDraggable}
                        onDragEnd={(event) => onPinDragEnd(event, marker)}
                        onClick={() => !currentDrawingMode && onIssueClick(marker)}
                        icon={createCustomIcon(marker)}
                        onMouseOver={() => onItemMouseOver({
                            ...marker,
                            position: position,
                        })}
                        onMouseOut={onItemMouseOut}
                    />
                    <OverlayView
                        position={position}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                        <div
                            style={getPinAnnotationStyle}
                        >
                            {index + 1}
                            {isItemDraggable && (
                                <FontAwesomeIcon
                                    icon={faUnlock}
                                    style={{marginLeft: "1px", fontSize: "8px"}}
                                />
                            )}
                        </div>
                    </OverlayView>
                </React.Fragment>
            ));
        });
    };

    function renderHoverInfo() {
        return <OverlayView
            position={hoveredItem?.position ? hoveredItem.position : {
                lat: Number(hoveredItem.address_lnglat.lat),
                lng: Number(hoveredItem.address_lnglat.lng),
            }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
            <div
                style={hoverItemStyle}
            >
                <div>
                    <strong>{hoveredItem.audit_type_item.annotation_name}</strong> <br />
                    <strong>Name: {hoveredItem.audit_type_item.name}</strong> <br />
                    {hoveredItem.length_feet && <>
                        <strong>Dimensions: {Number(hoveredItem.length_feet).toFixed(2)} ft</strong> <br />
                    </>}
                    {hoveredItem.area_feet && <>
                        <strong>Dimensions: {Number(hoveredItem.area_feet).toFixed(2)} ft²</strong> <br />
                    </>}
                    {hoveredItem.description && (
                        <>
                            <strong>
                                Description: {hoveredItem.description.length > 80 ?
                                    hoveredItem.description.slice(0, 80) + "..." : hoveredItem.description}
                            </strong>
                            <br />
                        </>)
                    }
                    <strong>Severity: {hoveredItem.severity_object.name}</strong> <br />
                    {!hoveredItem?.position && (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div
                                style={getItemStyle(getItemBackgroundColor())}
                            />
                            {
                                issuesSeveritiesWithColor?.find(
                                    (severity) => severity.id === Number(hoveredItem.severity),
                                )?.name
                            }
                        </div>
                    )}
                </div>
            </div>
        </OverlayView>;
    }

    function renderAreas() {
        return annotationAreas.map((area, index) => {
            const path = area.vertices_address_lnglat.map((path) =>
                ({lat: Number(path.lat), lng: Number(path.lng)})) ?? {};

            return (
                (auditsFilter.areas.includes(area.audit_type_item.name?.trim()) || auditsFilter.default) &&
                    <React.Fragment key={area.index}>
                        <Polygon
                            paths={path}
                            options={{
                                strokeColor: "white",
                                strokeOpacity: 1,
                                strokeWeight: 0.0,
                                fillColor: `${area.custom_color ?? area.audit_type_item?.color ?? "blue"}`,
                                fillOpacity: 0.7,
                                clickable: false,
                                editable: false,
                            }}
                        />
                        <OverlayView
                            position={getAreaLabelPosition(path)}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                            <div
                                onClick={(e) => {
                                    if (!isMobileDrawing && !currentDrawingMode) {
                                        e.stopPropagation();
                                        onAreaClick(area);
                                    }
                                }}
                                onMouseOver={() => {
                                    !isMobileDrawing && !currentDrawingMode &&
                                        onItemMouseOver({...area, position: getAreaLabelPosition(path)});
                                }}
                                onMouseOut={onItemMouseOut}
                                onTouchStart={(e) => {
                                    if (isMobileDrawing && !currentDrawingMode) {
                                        e.stopPropagation();
                                        onAreaClick(area);
                                    }
                                }}
                                style={getAreaAnnotationStyle(getBorderColor(area))}
                            >
                                A-{index + 1}
                            </div>
                        </OverlayView>
                    </React.Fragment>
            );
        });
    }

    function renderLines() {
        return annotationLines.map((line, index) => {
            let path = line.vertices_address_lnglat_ordered.map((path) =>
                ({lat: Number(path.lat), lng: Number(path.lng)})) ?? {};

            // backward compatibility with old structure
            if (path.length === 0) {
                const {address_lnglat1: addressLngLat1, address_lnglat2: addressLngLat2} = line;
                path = [
                    {lat: Number(addressLngLat1.lat), lng: Number(addressLngLat1.lng)},
                    {lat: Number(addressLngLat2.lat), lng: Number(addressLngLat2.lng)},
                ];
            }

            return (
                (auditsFilter.lines.includes(line.audit_type_item.name?.trim()) || auditsFilter.default) &&
                    <React.Fragment key={index}>
                        <Polyline
                            path={path}
                            options={{
                                strokeColor: `${line.custom_color ?? line.audit_type_item?.color ?? "red"}`,
                                strokeOpacity: 1,
                                strokeWeight: 3,
                                clickable: false,
                            }}
                        />
                        <OverlayView
                            position={getLineLabelPosition(path)}
                            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                            <div
                                onClick={(e) => {
                                    if (!isMobileDrawing && !currentDrawingMode) {
                                        e.stopPropagation();
                                        onLineClick(line);
                                    }
                                }}
                                onMouseOver={() => {
                                    !isMobileDrawing && !currentDrawingMode &&
                                        onItemMouseOver({...line, position: getLineLabelPosition(path)});
                                }}
                                onMouseOut={onItemMouseOut}
                                onTouchStart={(e) => {
                                    if (isMobileDrawing && !currentDrawingMode) {
                                        e.stopPropagation();
                                        onLineClick(line);
                                    }
                                }}
                                style={getLineAnnotationStyle(getBorderColor(line))}
                            >
                                L-{index + 1}
                            </div>
                        </OverlayView>
                    </React.Fragment>
            );
        });
    }

    function drawLine() {
        return <>
            {(linePoints.length > 0) && (
                <Polyline
                    path={linePoints}
                    options={LineDrawingOptions}
                />
            )}
            {drawingMousePosition && (
                <Marker
                    position={drawingMousePosition}
                    icon={LineCrosshairInnerIcon}
                />
            )}
        </>;
    }

    function drawArea() {
        return <>
            {areaPoints.length > 0 && (
                <Polygon
                    paths={areaPoints}
                    options={AreaDrawingOptions}
                />
            )}
            {isMobileDrawing && areaPoints.length < 3 && (
                <Polyline
                    path={areaPoints}
                    options={{
                        strokeColor: "blue",
                        strokeOpacity: areaPoints.length > 0 ? 0.6 : 0,
                        strokeWeight: 0,
                        icons: [
                            {
                                icon: AreaIcon,
                                offset: "0%",
                            },
                            {
                                icon: AreaIcon,
                                offset: "100%",
                            },
                        ],
                    }}
                />
            )}
            {drawingMousePosition && (
                <Marker
                    position={drawingMousePosition}
                    icon={AreaIcon}
                />
            )}
        </>;
    }

    function isSaveDrawingDisabled() {
        return (!currentDrawingMode ||
            currentDrawingMode === DrawingMode.POLYGON &&
                areaPoints.length < (3)) ||
            (currentDrawingMode === DrawingMode.POLYLINE &&
                linePoints.length < (2));
    }

    function isUndoDrawingDisabled() {
        return (!currentDrawingMode ||
                currentDrawingMode === DrawingMode.POLYGON &&
                areaPoints.length === 0) ||
            (currentDrawingMode === DrawingMode.POLYLINE &&
                linePoints.length === 0);
    }

    function getDrawingButtonBorderColor(isEnabled) {
        return isEnabled ? "3px solid black" : "3px solid darkgreen";
    }

    function renderDrawingToolbox() {
        return <>
            <button
                style={{
                    ...dragPinButtonStyle,
                    border: getDrawingButtonBorderColor(isUndoDrawingDisabled()),
                    color: isUndoDrawingDisabled() ? "darkgrey" : "darkred",
                    cursor: isDisableDragButton ? "not-allowed" : "pointer",
                    pointerEvents: isDisableDragButton ? "none" : "auto",
                }}
                disabled={isDisableDragButton}
            >
                <label
                    style={{
                        ...baseInputStyle,
                        cursor: isDisableDragButton ? "not-allowed" : "pointer",
                    }}
                >
                    <input
                        type="checkbox"
                        checked={isItemDraggable}
                        onChange={() => setIsItemDraggable(!isItemDraggable)}
                        disabled={isDisableDragButton}
                        style={{
                            ...baseInputStyle,
                            cursor: isDisableDragButton ? "not-allowed" : "pointer",
                        }}
                    />
                    Drag Pin
                </label>
            </button>
            <button style={{
                ...hideAllButtonStyle,
                border: "3px solid darkgreen",
            }}>
                <label style={baseInputStyle}>
                    <input
                        type="checkbox"
                        checked={isHideAllAnnotations}
                        onChange={() => setIsHideAllAnnotations(!isHideAllAnnotations)}
                        style={baseInputStyle}
                    />
                    Hide All
                </label>
            </button>
            {currentDrawingMode === DrawingMode.POLYGON && (
                <div style={currentAreaAndDistanceStyle}>
                    Area Drawing
                </div>
            )}
            {currentDrawingMode === DrawingMode.POLYLINE && (
                <div style={currentAreaAndDistanceStyle}>
                    Line Drawing
                </div>
            )}
            {currentLineLength && (
                <div style={currentAreaAndDistanceStyle}>
                    Dim (ft): {currentLineLength}
                </div>
            )}
            {calculatedAreaInFt && (
                <div style={currentAreaAndDistanceStyle}>
                    Dim (ft²): {calculatedAreaInFt}
                </div>
            )}
            {currentDrawingMode && <>
                <button
                    onClick={handleCancelDrawing}
                    style={{
                        ...cancelButtonStyle,
                        border: getDrawingButtonBorderColor(isUndoDrawingDisabled()),
                        color: isUndoDrawingDisabled() ? "darkgrey" : "darkred",
                    }}
                    disabled={isUndoDrawingDisabled()}
                >
                    <MdAutoDelete /> Cancel
                </button>
                <button
                    onClick={handleUndoDrawing}
                    style={{
                        ...undoButtonStyle,
                        border: getDrawingButtonBorderColor(isUndoDrawingDisabled()),
                        color: isUndoDrawingDisabled() ? "darkgrey" : "darkmagenta",
                    }}
                    disabled={isUndoDrawingDisabled()}
                >
                    <FaUndo /> Undo
                </button>
                <button
                    onClick={onSaveDrawing}
                    style={{
                        ...saveButtonStyle,
                        border: getDrawingButtonBorderColor(isSaveDrawingDisabled()),
                        color: isSaveDrawingDisabled() ? "darkgrey" : "darkblue",
                    }}
                    disabled={isSaveDrawingDisabled()}
                >
                    <FaSave /> Save
                    {isSavingLineOrArea &&
                    <div className="spinner-border spinner-border-sm" role="status" />
                    }
                </button>
            </>}
        </>;
    }

    function renderMapLocationToolbox() {
        return <>
            <Button
                onClick={goToCurrentLocation}
                style={currentLocationButtonStyle}
            >
                <FontAwesomeIcon icon={faLocationCrosshairs} />
            </Button>
            <button style={{
                ...locationButtonStyle,
                border: "3px solid darkgreen",
            }}>
                <label style={locationLabelStyle}>
                    <input
                        type="checkbox"
                        checked={isShowLocationMarker}
                        onChange={() => setIsShowLocationMarker(!isShowLocationMarker)}
                        style={locationInputStyle}
                    />
                    Location
                </label>
            </button>
        </>;
    }

    function getGoogleMapOptions() {
        return {
            mapTypeControl: !currentDrawingMode,
            mapTypeControlOptions: {position: google.maps.ControlPosition.TOP_LEFT},
            streetViewControl: true,
            mapTypeId: "satellite",
            fullscreenControl: false,
            fullscreenControlOptions: {position: google.maps.ControlPosition.BOTTOM_RIGHT},
            zoomControl: !currentDrawingMode,
            controlSize: 24,
            rotateControl: true,
            rotateControlOptions: {position: google.maps.ControlPosition.TOP_RIGHT},
            draggableCursor: "crosshair",
            tilt: 0,
            gestureHandling: isMobileDrawing ? "auto" : "greedy",
            disableDoubleClickZoom: true,
            disableClickZoom: true,
            keyboardShortcuts: false,
            cameraControl: false,
        };
    }

    function renderCurrentLocationMarker() {
        return <Marker
            position={currentClickedLocation}
            icon={{
                url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
                scaledSize: new window.google.maps.Size(40, 40),
            }}
        />;
    }

    function renderMobileCrosshair() {
        return (
            <>
                <OverlayView
                    position={mobileCrosshairPosition}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                    <div
                        style={crosshairContainerStyle}
                        onMouseDown={handleMobileCrosshairMouseDown}
                        onTouchStart={handleMobileCrosshairMouseDown}
                    >
                        <div
                            style={{
                                ...crosshairStyle,
                                backgroundImage: `url(${mapCrosshairIconMobile})`,
                            }}
                        />
                    </div>
                </OverlayView>
            </>
        );
    }

    return (
        <div style={containerStyle} id="map-container">
            <GoogleMap
                id={"google-map"}
                mapContainerStyle={containerStyle}
                center={currentLocation}
                zoom={getZoomLevel()}
                onLoad={onLoad}
                onUnmount={() => setMap(null)}
                options={getGoogleMapOptions()}
                onClick={!isViewOnly && !currentDrawingMode && isDisableMobileCrosshair() ? onMapClick : undefined}
            >
                {!!currentClickedLocation && isShowLocationMarker && renderCurrentLocationMarker()}
                {!!hoveredItem && !currentDrawingMode && renderHoverInfo()}
                {!isHideAllAnnotations && annotationMarkers.length > 0 && auditsFilter.pins && renderPins()}
                {!isHideAllAnnotations && annotationAreas.length > 0 && auditsFilter.areas && renderAreas()}
                {!isHideAllAnnotations && annotationLines.length > 0 && auditsFilter.lines && renderLines()}
                {currentDrawingMode === DrawingMode.POLYLINE ?
                    drawLine() :
                    currentDrawingMode === DrawingMode.POLYGON && drawArea()
                }
                {isMobileDrawing && mobileCrosshairPosition !== null && renderMobileCrosshair()}
            </GoogleMap>
            {renderMapLocationToolbox()}
            {!isViewOnly && renderDrawingToolbox()}
        </div>
    );
};

GSPSGoogleMap.propTypes = {
    audit: PropTypes.object.isRequired,
    annotationAreas: PropTypes.array.isRequired,
    annotationLines: PropTypes.array.isRequired,
    annotationMarkers: PropTypes.array.isRequired,
    onIssueClick: PropTypes.func.isRequired,
    onLineClick: PropTypes.func.isRequired,
    onAreaClick: PropTypes.func.isRequired,
    currentLocation: PropTypes.object,
    saveIssue: PropTypes.func,
    saveLine: PropTypes.func,
    saveArea: PropTypes.func,
    setMapClicked: PropTypes.func,
    currentAnnotationShape: PropTypes.object,
    currentAuditType: PropTypes.object,
    auditsFilter: PropTypes.object,
    handleDropDownResetDone: PropTypes.func,
    updateIssue: PropTypes.func,
    currentAuditTypeItem: PropTypes.object,
    isViewOnly: PropTypes.bool,
    issuesSeveritiesWithColor: PropTypes.array,
};
